import { computed, ref, shallowRef, readonly } from 'vue'
import { defineStore } from 'pinia'
import type { AdminDataScope, AdminUser, LoginRequest } from '@jiaofa/api-types'
import { AdminRole, Permission } from '@jiaofa/constants'
import { fetchCurrentUser, login as loginApi, logout as logoutApi } from '@/api/auth'
import { expandImplicitPermissions } from '@/utils/permissions'

const TOKEN_KEY = 'jiaofa-admin-token'
const USER_KEY = 'jiaofa-admin-user'

const permissionAliases: Record<string, string[]> = {
  [Permission.SystemRoleView]: [Permission.SystemRoleView, 'system:role:view'],
  [Permission.SystemRoleEdit]: [Permission.SystemRoleEdit, 'system:role:edit'],
}

const parseStoredUser = (): AdminUser | undefined => {
  const raw = localStorage.getItem(USER_KEY)
  if (!raw) return undefined
  try { return JSON.parse(raw) as AdminUser } catch { return undefined }
}

const persistUserSession = (nextToken: string, nextUser: AdminUser): void => {
  localStorage.setItem(TOKEN_KEY, nextToken)
  localStorage.setItem(USER_KEY, JSON.stringify(nextUser))
}

export const useUserStore = defineStore('admin-user', () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) ?? '')
  // 使用 shallowRef 优化大型用户对象
  const userInfo = shallowRef<AdminUser | undefined>(parseStoredUser())
  const isLoggedIn = computed(() => Boolean(token.value))
  const permissions = computed(() => expandImplicitPermissions(userInfo.value?.permissions ?? []))
  const dataScope = computed<AdminDataScope>(() => {
    if (userInfo.value?.dataScope) return userInfo.value.dataScope
    if (userInfo.value?.roles?.includes(AdminRole.SuperAdmin)) return 'ALL'
    return userInfo.value?.storeIds?.length ? 'STORE' : 'NONE'
  })
  const visibleStoreIds = computed(() => userInfo.value?.visibleStoreIds ?? userInfo.value?.storeIds ?? [])
  const isPlatformScope = computed(() => dataScope.value === 'ALL')

  const canAccessStore = (storeId: string): boolean =>
    isPlatformScope.value || visibleStoreIds.value.includes(storeId)

  const login = async (form: LoginRequest): Promise<void> => {
    if (!form.account.trim() || !form.password.trim()) throw new Error('请输入账号和密码')
    try {
      const result = await loginApi(form)
      token.value = result.token
      userInfo.value = result.user as AdminUser
      persistUserSession(token.value, userInfo.value)
    } catch (error) {
      if (import.meta.env.VITE_USE_MOCK !== 'true') throw error
      const mockUser: AdminUser = {
        id: 'mock-admin',
        nickname: '演示管理员',
        username: form.account,
        roles: ['SUPER_ADMIN'],
        permissions: ['*'],
        dataScope: 'ALL',
        visibleStoreIds: [],
      }
      token.value = 'mock-token'
      userInfo.value = mockUser
      persistUserSession(token.value, mockUser)
    }
  }

  const syncCurrentUser = async (): Promise<void> => {
    if (!token.value) return
    userInfo.value = await fetchCurrentUser()
    persistUserSession(token.value, userInfo.value)
  }

  const logout = async (): Promise<void> => {
    try {
      if (token.value) await logoutApi()
    } catch {
      // 忽略退出接口异常，确保前端会话总能清理
    }
    token.value = ''
    userInfo.value = undefined
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  }

  const hasEveryPermission = (required: string[]): boolean =>
    userInfo.value?.roles?.includes(AdminRole.SuperAdmin) === true ||
    permissions.value.includes('*') ||
    required.every((permission) =>
      (permissionAliases[permission] ?? [permission]).some((alias) => permissions.value.includes(alias)),
    )

  // 返回只读版本，防止外部直接修改
  return { 
    token: readonly(token), 
    userInfo: readonly(userInfo), 
    isLoggedIn, 
    permissions: readonly(permissions), 
    dataScope: readonly(dataScope),
    visibleStoreIds: readonly(visibleStoreIds),
    isPlatformScope,
    canAccessStore,
    login, 
    logout, 
    syncCurrentUser, 
    hasEveryPermission 
  }
})
